import type {
  ChoiceOption,
  MatrixRow,
  QuestionDefinition,
  QuestionnaireDraft,
  QuestionnaireExposureMark,
  QuestionnaireHttpOperation,
  QuestionnaireOperationAssetPatch,
  QuestionnaireOperationChoice,
  QuestionnaireOperationItem,
  QuestionnaireOperationMatrixRow,
  QuestionnaireOperationNewAsset,
  QuestionnaireOperationNewQuestion,
  QuestionnaireOperationQuestionPatch,
  QuestionnaireOperationResponse,
  QuestionnaireOperationTextProvenance,
  QuestionnaireOperationTextProvenanceFields,
  ResponseDefinition,
  TestedAsset,
  TestedAssetBlock,
} from "@digital-twins/shared-types";
import {
  competitorsAfterGridEdit,
  isAssetTextProvenance,
  type RosterGridEdit,
} from "@digital-twins/shared-types";

import { getConceptAssets, withEditableAssetRows } from "../assets/conceptAssets";

/**
 * A draft's content as a string for comparison: keys sorted, write
 * timestamps and empty lists left out. Two drafts with the same content
 * compare equal whatever their key order or save time.
 */
export function draftContentFingerprint(value: unknown): string {
  return JSON.stringify(value, function (this: unknown, key, inner) {
    if (key === "updatedAt") return undefined;
    // An empty list on an object says the same as no list.
    if (Array.isArray(inner) && inner.length === 0 && isRecord(this))
      return undefined;
    return isRecord(inner)
      ? Object.fromEntries(
          Object.keys(inner)
            .sort()
            .map((entry) => [entry, inner[entry]]),
        )
      : inner;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * The builder's unsaved edits as the typed operations one Save sends
 * (`POST /questionnaire-drafts/:id/operations`, plan Stage 1, S1.5).
 *
 * Pure: `base` is the accepted draft the edits started from, `local` the
 * builder's copy. Only fields the operations carry are compared, so a
 * server-derived field (question and item `assetRefs`, `metricRefs`,
 * `slideRefs`, `tags`, `planAttribution`, `postReveal`,
 * `placeholderSubstitutions`, ids, asset `metadata` other than its text
 * provenance and scene manifest, timestamps) never makes an operation. An
 * undo/redo round trip back to `base` diffs to nothing.
 *
 * Order: asset removals, additions, media and edits first (so a question can
 * name a new asset and a cap counts the final catalog), then question deletions, the
 * fewest moves that restore the kept questions' order, additions after their
 * local predecessor, type changes and field edits, and the exposure marks last,
 * because they name questions in the final list.
 */
export function diffDraftToOperations(
  base: QuestionnaireDraft,
  local: QuestionnaireDraft,
): Array<QuestionnaireHttpOperation> {
  const brandEdit = base.brandFacts
    ? competitorsAfterGridEdit({
        brandFacts: base.brandFacts,
        saved: base.researchQuestions ?? [],
        local: local.researchQuestions ?? [],
      })
    : null;
  const questions = questionOperations(base, followingBrands(base, local, brandEdit));
  return [
    ...surveyMetaOperations(base, local),
    ...diffTestedAssetsToOperations(base.testedAssets, local.testedAssets),
    ...brandOperations(base, brandEdit),
    ...questions,
    ...exposureOperations(base, local, questions),
  ];
}

/** Content equality: key order, write timestamps and empty lists never count. */
function same(left: unknown, right: unknown): boolean {
  return (
    draftContentFingerprint({ value: left }) ===
    draftContentFingerprint({ value: right })
  );
}

function surveyMetaOperations(
  base: QuestionnaireDraft,
  local: QuestionnaireDraft,
): Array<QuestionnaireHttpOperation> {
  const meta: { title?: string; description?: string } = {};
  if ((local.title ?? "") !== (base.title ?? "")) meta.title = local.title ?? "";
  if ((local.description ?? "") !== (base.description ?? ""))
    meta.description = local.description ?? "";
  return Object.keys(meta).length > 0
    ? [{ op: "set_survey_meta", ...meta }]
    : [];
}

/**
 * The brands a derived brand grid's edit adds or removes are the study's
 * brands: they travel in `set_study_context`, the brand facts' one writer, and
 * every question derived from them follows. The live check asks the cap of the
 * same names (`competitorsAfterGridEdit`).
 */
function brandOperations(
  base: QuestionnaireDraft,
  edit: RosterGridEdit | null,
): Array<QuestionnaireHttpOperation> {
  return edit && base.brandFacts
    ? [
        {
          op: "set_study_context",
          brands: { ...base.brandFacts, competitors: edit.competitors },
        },
      ]
    : [];
}

/**
 * A derived grid whose only change is a brand joining or leaving sends no
 * rows: the brands carry that change and the grid follows them by derivation,
 * so its rows are the saved ones in the diff. Any other change to its rows
 * (a rename, a move, a row removed while its brand stays) is sent as written
 * and makes the grid the author's own; its measures and columns travel either way.
 */
function followingBrands(
  base: QuestionnaireDraft,
  local: QuestionnaireDraft,
  edit: RosterGridEdit | null,
): QuestionnaireDraft {
  if (!edit || edit.followedKeys.length === 0) return local;
  const saved = new Map(
    (base.researchQuestions ?? []).map((question) => [question.stableKey, question]),
  );
  return {
    ...local,
    researchQuestions: (local.researchQuestions ?? []).map((question) => {
      const was = saved.get(question.stableKey);
      return edit.followedKeys.includes(question.stableKey) && was
        ? ({
            ...question,
            response: {
              ...question.response,
              rows: (was.response as { rows: unknown }).rows,
            },
          } as QuestionDefinition)
        : question;
    }),
  };
}

// ── Assets ──────────────────────────────────────────────────────────────────

/** Fields the server owns, or that travel in their own operation. */
const ASSET_FIELDS_NOT_PATCHED = new Set([
  "id",
  "stableKey",
  "media",
  "metadata",
  "createdAt",
  "updatedAt",
  "twinFacingLabel",
]);

const ASSET_FIELDS_NOT_SENT = new Set([
  "id",
  "metadata",
  "createdAt",
  "updatedAt",
  "twinFacingLabel",
]);

function assetsOf(block: TestedAssetBlock | null | undefined): Array<TestedAsset> {
  const assets = block?.assets;
  return Array.isArray(assets) ? assets : [];
}

const TEXT_FIELDS = ["name", "description"] as const;

/** How `name`/`description` were written, as recorded in `metadata.authored`. */
function recordedProvenance(
  asset: TestedAsset,
  field: (typeof TEXT_FIELDS)[number],
): QuestionnaireOperationTextProvenance | undefined {
  const value = (
    asset.metadata?.authored as Record<string, unknown> | undefined
  )?.[field];
  return isAssetTextProvenance(value) ? value : undefined;
}

/** The scenes a video read found (`metadata.sceneManifest`). */
function sceneManifestOf(asset: TestedAsset) {
  return (asset.metadata?.sceneManifest ??
    undefined) as QuestionnaireOperationNewAsset["sceneManifest"];
}

function newAsset(asset: TestedAsset): QuestionnaireOperationNewAsset {
  const sent = Object.fromEntries(
    Object.entries(asset).filter(
      ([key, value]) => !ASSET_FIELDS_NOT_SENT.has(key) && value !== undefined,
    ),
  );
  const textProvenance: QuestionnaireOperationTextProvenanceFields = {};
  for (const field of TEXT_FIELDS) {
    const provenance = recordedProvenance(asset, field);
    if (provenance) textProvenance[field] = provenance;
  }
  const sceneManifest = sceneManifestOf(asset);
  return {
    ...sent,
    media: Array.isArray(asset.media) ? asset.media : [],
    ...(Object.keys(textProvenance).length > 0 ? { textProvenance } : {}),
    ...(sceneManifest ? { sceneManifest } : {}),
  } as QuestionnaireOperationNewAsset;
}

function assetPatch(
  base: TestedAsset,
  local: TestedAsset,
): QuestionnaireOperationAssetPatch {
  const before = base as unknown as Record<string, unknown>;
  const after = local as unknown as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) {
    if (ASSET_FIELDS_NOT_PATCHED.has(key)) continue;
    if (same(before[key], after[key])) continue;
    patch[key] = after[key] === undefined ? null : after[key];
  }
  // Who wrote a changed name or description travels with it: a video read's
  // text is `generated`, so a later read may replace it; the marketer's never
  // is. A provenance change on unchanged text travels too.
  const textProvenance: QuestionnaireOperationTextProvenanceFields = {};
  for (const field of TEXT_FIELDS) {
    const provenance = recordedProvenance(local, field);
    const changed =
      field in patch || provenance !== recordedProvenance(base, field);
    if (changed && provenance) textProvenance[field] = provenance;
  }
  if (Object.keys(textProvenance).length > 0)
    patch.textProvenance = textProvenance;
  if (!same(sceneManifestOf(base) ?? null, sceneManifestOf(local) ?? null))
    patch.sceneManifest = sceneManifestOf(local) ?? null;
  return patch as QuestionnaireOperationAssetPatch;
}

/**
 * The replay rule of an intake asset write (`persistOperations`): the
 * accepted draft already carries the write when its assets diff to nothing
 * against the write's target, as after a replay whose first response was
 * lost.
 */
export function carriesTestedAssets(
  target: TestedAssetBlock | null | undefined,
): (latest: QuestionnaireDraft) => boolean {
  return (latest) =>
    diffTestedAssetsToOperations(latest.testedAssets, target).length === 0;
}

/**
 * The asset half of the diff: what an intake write (collect card, composer
 * asset, video read) sends, since those change the catalog and nothing else.
 */
export function diffTestedAssetsToOperations(
  base: TestedAssetBlock | null | undefined,
  local: TestedAssetBlock | null | undefined,
): Array<QuestionnaireHttpOperation> {
  const baseAssets = assetsOf(base);
  const localAssets = assetsOf(local);
  const baseById = new Map(baseAssets.map((asset) => [asset.id, asset]));
  const localIds = new Set(localAssets.map((asset) => asset.id));
  const operations: Array<QuestionnaireHttpOperation> = [];
  for (const asset of baseAssets) {
    if (!localIds.has(asset.id))
      operations.push({ op: "remove_asset", target: asset.stableKey });
  }
  for (const asset of localAssets) {
    if (!baseById.has(asset.id))
      operations.push({ op: "add_asset", asset: newAsset(asset) });
  }
  for (const asset of localAssets) {
    const before = baseById.get(asset.id);
    if (!before) continue;
    // Media first: replacing a video drops its scenes on the server, so the
    // scenes of a read of the new video have to land after it.
    if (!same(before.media ?? [], asset.media ?? []))
      operations.push({
        op: "set_asset_media",
        target: before.stableKey,
        media: asset.media ?? [],
      });
    const patch = assetPatch(before, asset);
    if (Object.keys(patch).length > 0)
      operations.push({ op: "edit_asset", target: before.stableKey, patch });
  }
  const baseTypes = base?.assetTypes ?? [];
  const localTypes = local?.assetTypes ?? [];
  if (!same(baseTypes, localTypes))
    operations.push({
      op: "set_declared_asset_types",
      assetTypes: [...localTypes],
    });
  return operations;
}

// ── Questions ───────────────────────────────────────────────────────────────

function operationChoice(option: ChoiceOption): QuestionnaireOperationChoice {
  return {
    id: option.id,
    value: option.value,
    label: option.label,
    ...(option.description !== undefined
      ? { description: option.description }
      : {}),
    ...(option.exclusive !== undefined ? { exclusive: option.exclusive } : {}),
    ...(option.keepLast === true ? { keepLast: true } : {}),
  };
}

/**
 * A ranking item. The contract gives it no `exclusive`: an escape item in a
 * ranked list means nothing (D0a), and the builder never offers one there. A
 * ranking pins nothing last, so no `keepLast`.
 */
function operationItem(option: ChoiceOption): QuestionnaireOperationItem {
  return {
    id: option.id,
    value: option.value,
    label: option.label,
    ...(option.description !== undefined
      ? { description: option.description }
      : {}),
  };
}

function operationRow(row: MatrixRow): QuestionnaireOperationMatrixRow {
  return {
    id: row.id,
    value: row.value,
    label: row.label,
    ...(row.description !== undefined ? { description: row.description } : {}),
  };
}

/**
 * An existing question's response as an edit carries it. Besides the item
 * refs, a typed roster's link (`optionSource`) is the server's: the applier
 * keeps it while the asset answers stay the same items, so the editor never
 * decides it (plan 09 S3.3).
 */
function editedResponse(
  response: ResponseDefinition,
): QuestionnaireOperationResponse {
  const { optionSource: _serverOwned, ...edited } = operationResponse(
    response,
  ) as QuestionnaireOperationResponse & { optionSource?: unknown };
  return edited as QuestionnaireOperationResponse;
}

/** The response as the wire carries it: item refs are server-derived. */
export function operationResponse(
  response: ResponseDefinition,
): QuestionnaireOperationResponse {
  const out = JSON.parse(JSON.stringify(response)) as Record<string, unknown>;
  if (Array.isArray(out.options))
    out.options = (out.options as Array<ChoiceOption>).map(
      response.kind === "ranking" ? operationItem : operationChoice,
    );
  // A grid's categories carry their escape and keep-last flags as a select's
  // options do: the flagged ones stay after the shuffled categories.
  if (Array.isArray(out.categories))
    out.categories = (out.categories as Array<ChoiceOption>).map(
      operationChoice,
    );
  const rows = out.rows as
    | { source?: string; rows?: Array<MatrixRow> }
    | undefined;
  if (rows?.source === "fixed" && Array.isArray(rows.rows))
    out.rows = { source: "fixed", rows: rows.rows.map(operationRow) };
  return out as unknown as QuestionnaireOperationResponse;
}

function newQuestion(
  question: QuestionDefinition,
): QuestionnaireOperationNewQuestion {
  return {
    stableKey: question.stableKey,
    type: question.type,
    title: question.title,
    ...(question.description !== undefined
      ? { description: question.description }
      : {}),
    required: question.required,
    ...(question.awarenessEntities
      ? { awarenessEntities: question.awarenessEntities }
      : {}),
    response: operationResponse(question.response),
    ...(question.assetBinding ? { assetBinding: question.assetBinding } : {}),
    ...(question.visibilityRule
      ? { visibilityRule: question.visibilityRule }
      : {}),
    ...(question.routingRule ? { routingRule: question.routingRule } : {}),
  };
}

function questionPatch(
  base: QuestionDefinition,
  local: QuestionDefinition,
  includeResponse: boolean,
  assets: ReturnType<typeof getConceptAssets>,
): QuestionnaireOperationQuestionPatch {
  const patch: QuestionnaireOperationQuestionPatch = {};
  if ((local.title ?? "") !== (base.title ?? "")) patch.title = local.title;
  if ((local.description ?? "") !== (base.description ?? ""))
    patch.description = local.description ?? "";
  if (Boolean(local.required) !== Boolean(base.required))
    patch.required = Boolean(local.required);
  if (!same(local.awarenessEntities ?? null, base.awarenessEntities ?? null))
    patch.awarenessEntities = local.awarenessEntities ?? null;
  // A grid on the tested assets turns into the author's own rows the moment a
  // row is touched (`withEditableAssetRows`); rows that are again exactly
  // those the assets give are no edit, so an added row removed again leaves
  // the grid bound to its assets (F34).
  const baseResponse = withEditableAssetRows(base, assets).response;
  if (
    includeResponse &&
    !same(local.response, base.response) &&
    !same(editedResponse(local.response), editedResponse(base.response)) &&
    !same(editedResponse(local.response), editedResponse(baseResponse))
  )
    patch.response = editedResponse(local.response);
  if (!same(local.visibilityRule ?? null, base.visibilityRule ?? null))
    patch.visibilityRule = local.visibilityRule ?? null;
  if (!same(local.routingRule ?? null, base.routingRule ?? null))
    patch.routingRule = local.routingRule ?? null;
  return patch;
}

/**
 * Indices (into `sequence`) of one longest strictly increasing subsequence.
 * The kept questions it names stay put; every other kept question moves.
 */
function longestIncreasingRun(sequence: Array<number>): Set<number> {
  const tails: Array<number> = [];
  const previous: Array<number> = new Array(sequence.length).fill(-1);
  sequence.forEach((value, index) => {
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const middle = (low + high) >> 1;
      if (sequence[tails[middle]] < value) low = middle + 1;
      else high = middle;
    }
    if (low > 0) previous[index] = tails[low - 1];
    tails[low] = index;
  });
  const run = new Set<number>();
  let cursor = tails.length > 0 ? tails[tails.length - 1] : -1;
  while (cursor >= 0) {
    run.add(cursor);
    cursor = previous[cursor];
  }
  return run;
}

function questionOperations(
  base: QuestionnaireDraft,
  local: QuestionnaireDraft,
): Array<QuestionnaireHttpOperation> {
  const baseQuestions = base.researchQuestions ?? [];
  const localQuestions = local.researchQuestions ?? [];
  const assets = getConceptAssets(local.testedAssets);
  const baseByKey = new Map(
    baseQuestions.map((question) => [question.stableKey, question]),
  );
  const localKeys = new Set(localQuestions.map((question) => question.stableKey));
  const operations: Array<QuestionnaireHttpOperation> = [];

  for (const question of baseQuestions) {
    if (!localKeys.has(question.stableKey))
      operations.push({ op: "delete_question", target: question.stableKey });
  }

  // Kept questions in their local order, each with its base position.
  const keptLocal = localQuestions.filter((question) =>
    baseByKey.has(question.stableKey),
  );
  const baseIndex = new Map(
    baseQuestions
      .filter((question) => localKeys.has(question.stableKey))
      .map((question, index) => [question.stableKey, index]),
  );
  const stay = longestIncreasingRun(
    keptLocal.map((question) => baseIndex.get(question.stableKey) as number),
  );
  keptLocal.forEach((question, index) => {
    if (stay.has(index)) return;
    operations.push({
      op: "move_question",
      target: question.stableKey,
      position: {
        kind: "after",
        ref: index === 0 ? "start" : keptLocal[index - 1].stableKey,
      },
    });
  });

  localQuestions.forEach((question, index) => {
    if (baseByKey.has(question.stableKey)) return;
    operations.push({
      op: "add_question",
      question: newQuestion(question),
      position: {
        kind: "after",
        ref: index === 0 ? "start" : localQuestions[index - 1].stableKey,
      },
    });
  });

  for (const question of keptLocal) {
    const before = baseByKey.get(question.stableKey) as QuestionDefinition;
    const typeChanged = before.type !== question.type;
    if (typeChanged)
      operations.push({
        op: "change_type",
        target: question.stableKey,
        newType: question.type,
        response: editedResponse(question.response),
      });
    const patch = questionPatch(before, question, !typeChanged, assets);
    if (Object.keys(patch).length > 0)
      operations.push({
        op: "edit_question",
        target: question.stableKey,
        patch,
      });
  }
  return operations;
}

// ── Exposure ────────────────────────────────────────────────────────────────

function markChanged(
  base: QuestionnaireExposureMark | null | undefined,
  local: QuestionnaireExposureMark | null | undefined,
): local is QuestionnaireExposureMark {
  if (!local) return false;
  return (
    !base || base.stableKey !== local.stableKey || base.source !== local.source
  );
}

/**
 * The line moves the batch states. A mark the edits moved, and a mark on a
 * question the batch moves: the server re-anchors a line whose question moves
 * (`exposureBoundaryBeforeMove`), and the minimal moves need not be the drag
 * the builder showed, so the builder states where its line is.
 */
function exposureOperations(
  base: QuestionnaireDraft,
  local: QuestionnaireDraft,
  questions: Array<QuestionnaireHttpOperation>,
): Array<QuestionnaireHttpOperation> {
  const before = base.exposureBoundary;
  const after = local.exposureBoundary;
  const moved = new Set(
    questions.flatMap((operation) =>
      operation.op === "move_question" ? [operation.target] : [],
    ),
  );
  const onMoved = (mark: QuestionnaireExposureMark | null | undefined) =>
    !!mark?.stableKey && moved.has(mark.stableKey);
  const marks: { reveal?: string | null; sharedView?: string | null } = {};
  if (markChanged(before?.reveal, after?.reveal) || (after?.reveal && onMoved(before?.reveal)))
    marks.reveal = after!.reveal!.stableKey;
  if (
    markChanged(before?.sharedView, after?.sharedView) ||
    (after?.sharedView && onMoved(before?.sharedView))
  )
    marks.sharedView = after!.sharedView!.stableKey;
  return Object.keys(marks).length > 0
    ? [{ op: "set_exposure_boundary", ...marks }]
    : [];
}
